export interface ParsedUserImportRow {
  email: string;
  firstName: string;
  lastName: string;
  roles: string;
  isActive: boolean;
  errors: string[];
}

type SupportedDelimiter = ';' | ',';
type HeaderKey = 'email' | 'firstName' | 'lastName' | 'roles' | 'isActive';

interface HeaderMatchCandidate {
  index: number;
  delimiter: SupportedDelimiter;
  columns: string[];
  score: number;
}

const SUPPORTED_DELIMITERS: SupportedDelimiter[] = [';', ','];

const HEADER_ALIASES: Record<HeaderKey, string[]> = {
  email: ['email', 'epost', 'e-post'],
  firstName: ['firstname', 'first_name', 'fornamn', 'förnamn'],
  lastName: ['lastname', 'last_name', 'efternamn'],
  roles: ['roles', 'roller', 'role', 'roll'],
  isActive: ['isactive', 'active', 'aktiv'],
};

const normalizeHeader = (value: string): string => (
  value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
);

const resolveHeaderKey = (header: string): HeaderKey | null => {
  const normalized = normalizeHeader(header);

  for (const [key, aliases] of Object.entries(HEADER_ALIASES) as [HeaderKey, string[]][]) {
    if (aliases.includes(normalized)) {
      return key;
    }
  }

  return null;
};

const parseDelimitedLine = (line: string, delimiter: SupportedDelimiter): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const detectHeaderRow = (lines: string[]): { index: number; delimiter: SupportedDelimiter; columns: string[] } | null => {
  let bestMatch: HeaderMatchCandidate | null = null;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();
    if (!trimmedLine || trimmedLine.startsWith('#')) {
      continue;
    }

    for (const delimiter of SUPPORTED_DELIMITERS) {
      const columns = parseDelimitedLine(trimmedLine, delimiter);
      const recognizedHeaders = new Set(columns.map(resolveHeaderKey).filter(Boolean) as HeaderKey[]);
      const score = recognizedHeaders.size;

      if (!recognizedHeaders.has('email') || score === 0) {
        continue;
      }

      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { index, delimiter, columns, score };
      }
    }
  }

  const match = bestMatch;
  if (!match) {
    return null;
  }

  return {
    index: match.index,
    delimiter: match.delimiter,
    columns: match.columns,
  };
};

const parseBooleanCell = (value: string | undefined): { value: boolean; error?: string } => {
  const normalized = (value || '').trim().toLowerCase();

  if (!normalized) {
    return { value: true };
  }

  if (['true', '1', 'yes', 'ja', 'aktiv'].includes(normalized)) {
    return { value: true };
  }

  if (['false', '0', 'no', 'nej', 'inaktiv'].includes(normalized)) {
    return { value: false };
  }

  return {
    value: true,
    error: `Ogiltigt värde för Aktiv: "${value}"`,
  };
};

export const parseUserImportCsv = (text: string): ParsedUserImportRow[] => {
  const normalizedText = text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const lines = normalizedText.split('\n');
  const headerMatch = detectHeaderRow(lines);

  if (!headerMatch) {
    return [];
  }

  const headerMap = new Map<HeaderKey, number>();
  headerMatch.columns.forEach((header, index) => {
    const key = resolveHeaderKey(header);
    if (key && !headerMap.has(key)) {
      headerMap.set(key, index);
    }
  });

  if (!headerMap.has('email')) {
    return [];
  }

  const parsedUsers: ParsedUserImportRow[] = [];

  for (let lineIndex = headerMatch.index + 1; lineIndex < lines.length; lineIndex += 1) {
    const rawLine = lines[lineIndex];
    const trimmedLine = rawLine.trim();

    if (!trimmedLine || trimmedLine.startsWith('#')) {
      continue;
    }

    const values = parseDelimitedLine(trimmedLine, headerMatch.delimiter);
    const email = (values[headerMap.get('email') || 0] || '').trim().toLowerCase();

    if (!email) {
      continue;
    }

    const activeParse = parseBooleanCell(
      headerMap.has('isActive') ? values[headerMap.get('isActive') as number] : undefined
    );

    const errors = activeParse.error ? [activeParse.error] : [];

    parsedUsers.push({
      email,
      firstName: headerMap.has('firstName') ? (values[headerMap.get('firstName') as number] || '').trim() : '',
      lastName: headerMap.has('lastName') ? (values[headerMap.get('lastName') as number] || '').trim() : '',
      roles: headerMap.has('roles') ? (values[headerMap.get('roles') as number] || '').trim() : '',
      isActive: activeParse.value,
      errors,
    });
  }

  return parsedUsers;
};
